import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCalendarWorkspaceRealtime } from '../../hooks/useCalendarWorkspaceRealtime';
import {
  CalendarRecurrenceFields,
  emptyRecurrenceForm,
  recurrenceFormFromRule,
  ruleFromRecurrenceForm,
  type RecurrenceFormState,
} from '../../components/calendar/CalendarRecurrenceFields';
import {
  calendarPersonLabel,
  calendarStatusLabel,
  calendarTodayYmd,
  createCalendarTask,
  DEFAULT_CALENDAR_SLUG,
  deleteCalendarTask,
  deleteCalendarTaskAttachment,
  getCalendarAttachmentSignedUrl,
  isCalendarTaskOverdue,
  listCalendarDirectory,
  listCalendarTaskAttachments,
  listCalendarTasks,
  listThreadTasks,
  sortThreadTasksByDate,
  saveCalendarTaskEdit,
  type CalendarSeriesEditScope,
  uploadCalendarTaskAttachment,
} from '../../lib/calendarWorkspace';
import { recurrenceRuleLabel } from '../../lib/calendarRecurrence';
import type {
  CalendarTask,
  CalendarTaskAttachment,
  CalendarTaskStatus,
} from '../../types/calendarWorkspace';

type GroupMode = 'date' | 'task';

function formatGroupDate(day: string): string {
  const today = calendarTodayYmd();
  const d = new Date(`${day}T12:00:00`);
  const label = d.toLocaleDateString('en-SG', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  if (day === today) return `Today · ${label}`;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomYmd = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
  if (day === tomYmd) return `Tomorrow · ${label}`;
  return label;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatMonthGroup(monthKey: string): string {
  if (monthKey === 'undated') return 'Undated';
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, (m ?? 1) - 1, 1);
  return d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
}

function statusSort(a: CalendarTask, b: CalendarTask): number {
  const aDone = a.status === 'done' ? 1 : 0;
  const bDone = b.status === 'done' ? 1 : 0;
  if (aDone !== bDone) return aDone - bDone;
  return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
}

export function CalendarTasksPage() {
  const { slug = DEFAULT_CALENDAR_SLUG } = useParams<{ slug: string }>();
  const { session } = useAuth();
  const email = session?.user?.email ?? '';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tasks, setTasks] = useState<CalendarTask[]>([]);
  const [directory, setDirectory] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [groupMode, setGroupMode] = useState<GroupMode>('date');
  const [selected, setSelected] = useState<CalendarTask | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CalendarTask | null>(null);
  const [timeline, setTimeline] = useState<CalendarTask[]>([]);
  const [attachments, setAttachments] = useState<CalendarTaskAttachment[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [recurForm, setRecurForm] = useState<RecurrenceFormState>(emptyRecurrenceForm());
  const [editRecurForm, setEditRecurForm] = useState<RecurrenceFormState>(emptyRecurrenceForm());
  const [seriesScope, setSeriesScope] = useState<CalendarSeriesEditScope>('this');
  const [form, setForm] = useState({
    title: '',
    description: '',
    request_date: calendarTodayYmd(),
    todo_date: calendarTodayYmd(),
    deadline: calendarTodayYmd(),
    assignee_email: email,
    status: 'todo' as CalendarTaskStatus,
  });

  const today = calendarTodayYmd();
  const personName = (value: string | null | undefined) => calendarPersonLabel(value, directory);

  const load = async () => {
    try {
      const [taskRows, dir] = await Promise.all([
        listCalendarTasks(slug),
        listCalendarDirectory().catch(() => new Map<string, string>()),
      ]);
      setTasks(taskRows);
      setDirectory(dir);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tasks.');
    }
  };

  const loadAttachments = async (task: CalendarTask) => {
    try {
      setAttachments(await listCalendarTaskAttachments(task.id));
    } catch {
      setAttachments([]);
    }
  };

  useEffect(() => {
    void load();
  }, [slug]);

  useCalendarWorkspaceRealtime({
    workspaceSlug: slug,
    onChange: () => {
      void load();
    },
  });

  useEffect(() => {
    if (email && !form.assignee_email) setForm((f) => ({ ...f, assignee_email: email }));
  }, [email]);

  useEffect(() => {
    if (!selected) {
      setTimeline([]);
      setDraft(null);
      setEditing(false);
      setAttachments([]);
      setEditRecurForm(emptyRecurrenceForm());
      return;
    }
    setDraft(selected);
    setEditing(false);
    setEditRecurForm(recurrenceFormFromRule(selected.recurrence_rule));
    setSeriesScope('this');
    void listThreadTasks(selected.thread_id).then(setTimeline).catch(() => setTimeline([selected]));
    void loadAttachments(selected);
  }, [selected]);

  useEffect(() => {
    setCollapsed({});
  }, [groupMode]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) => {
      const hay = [
        t.title,
        t.description,
        t.assignee_email,
        personName(t.assignee_email),
        t.status,
        calendarStatusLabel(t.status),
        t.todo_date,
        t.deadline,
        t.request_date,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [tasks, query, directory]);

  const monthGroups = useMemo(() => {
    const byMonth = new Map<string, Map<string, CalendarTask[]>>();
    for (const t of filtered) {
      const day = t.todo_date || 'undated';
      const month = day === 'undated' ? 'undated' : day.slice(0, 7);
      const days = byMonth.get(month) ?? new Map<string, CalendarTask[]>();
      const list = days.get(day) ?? [];
      list.push(t);
      days.set(day, list);
      byMonth.set(month, days);
    }

    return [...byMonth.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([month, days]) => {
        const dayGroups = [...days.entries()]
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([date, list]) => ({
            key: `day:${date}`,
            date,
            title: formatGroupDate(date),
            count: list.length,
            tasks: [...list].sort(statusSort),
          }));
        return {
          key: `month:${month}`,
          month,
          title: formatMonthGroup(month),
          count: dayGroups.reduce((n, d) => n + d.count, 0),
          days: dayGroups,
        };
      });
  }, [filtered]);

  const taskGroups = useMemo(() => {
    const map = new Map<string, CalendarTask[]>();
    for (const t of filtered) {
      const list = map.get(t.thread_id) ?? [];
      list.push(t);
      map.set(t.thread_id, list);
    }
    return [...map.entries()]
      .map(([threadId, list]) => {
        const sorted = [...list].sort((a, b) => {
          if (a.thread_order !== b.thread_order) return a.thread_order - b.thread_order;
          return a.todo_date.localeCompare(b.todo_date);
        });
        const root = sorted[0];
        const subs = sorted.slice(1);
        return {
          key: threadId,
          title: root?.title ?? 'Task',
          count: sorted.length,
          root,
          tasks: sorted,
          subtasks: subs,
          requestDate: root?.request_date ?? '',
        };
      })
      .sort((a, b) => {
        const rd = a.requestDate.localeCompare(b.requestDate);
        if (rd !== 0) return rd;
        return (a.root?.title ?? '').localeCompare(b.root?.title ?? '');
      });
  }, [filtered]);

  const toggleGroup = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => setCollapsed({});
  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    if (groupMode === 'date') {
      for (const month of monthGroups) {
        next[month.key] = true;
        for (const day of month.days) next[day.key] = true;
      }
    } else {
      for (const g of taskGroups) next[g.key] = true;
    }
    setCollapsed(next);
  };

  const create = async () => {
    if (!form.title.trim() || !email) return;
    setBusy(true);
    setError(null);
    try {
      const task = await createCalendarTask(
        slug,
        {
          title: form.title,
          description: form.description,
          request_date: form.request_date,
          todo_date: form.todo_date,
          deadline: form.deadline,
          assignee_email: form.assignee_email,
          status: form.status,
          recurrence_rule: ruleFromRecurrenceForm(recurForm),
        },
        email,
      );
      setForm({
        title: '',
        description: '',
        request_date: calendarTodayYmd(),
        todo_date: calendarTodayYmd(),
        deadline: calendarTodayYmd(),
        assignee_email: email,
        status: 'todo',
      });
      setRecurForm(emptyRecurrenceForm());
      setShowCreate(false);
      await load();
      setSelected(task);
      setEditing(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed.');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const updated = await saveCalendarTaskEdit({
        id: draft.id,
        patch: {
          title: draft.title,
          description: draft.description,
          status: draft.status,
          request_date: draft.request_date,
          todo_date: draft.todo_date,
          deadline: draft.deadline,
          assignee_email: draft.assignee_email,
        },
        recurrenceRule: ruleFromRecurrenceForm(editRecurForm),
        seriesScope: draft.recurrence_series_id ? seriesScope : 'this',
      });
      setSelected(updated);
      setDraft(updated);
      setEditRecurForm(recurrenceFormFromRule(updated.recurrence_rule));
      setSeriesScope('this');
      setEditing(false);
      await load();
      setTimeline(await listThreadTasks(updated.thread_id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const cancelEdit = () => {
    setDraft(selected);
    setEditRecurForm(recurrenceFormFromRule(selected?.recurrence_rule));
    setEditing(false);
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this task?')) return;
    setBusy(true);
    try {
      await deleteCalendarTask(id);
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  const followUp = async () => {
    if (!selected || !email) return;
    setBusy(true);
    try {
      const base = new Date(`${selected.todo_date}T12:00:00`);
      base.setDate(base.getDate() + 2);
      const y = base.getFullYear();
      const m = String(base.getMonth() + 1).padStart(2, '0');
      const d = String(base.getDate()).padStart(2, '0');
      const next = `${y}-${m}-${d}`;
      const first = timeline[0] ?? selected;
      const task = await createCalendarTask(
        slug,
        {
          title: `${first.title.replace(/\s—\sfollow-up$/i, '')} — follow-up`,
          request_date: first.request_date,
          todo_date: next,
          deadline: first.deadline,
          assignee_email: selected.assignee_email || email,
          thread_id: selected.thread_id,
        },
        email,
      );
      await load();
      setSelected(task);
      setEditing(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Follow-up failed.');
    } finally {
      setBusy(false);
    }
  };

  const onAttachFiles = async (files: FileList | null) => {
    if (!selected || !email || !files?.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await uploadCalendarTaskAttachment({ task: selected, file, actorEmail: email });
      }
      await loadAttachments(selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed.');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openAttachment = async (att: CalendarTaskAttachment) => {
    try {
      const url = await getCalendarAttachmentSignedUrl(att.storage_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open file.');
    }
  };

  const removeAttachment = async (att: CalendarTaskAttachment) => {
    if (!confirm(`Remove “${att.file_name}”?`)) return;
    setBusy(true);
    try {
      await deleteCalendarTaskAttachment(att);
      if (selected) await loadAttachments(selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove file.');
    } finally {
      setBusy(false);
    }
  };

  const view = draft ?? selected;
  const modeLabel = groupMode === 'date' ? 'by to-do date' : 'by task · request date';

  const renderTaskRow = (t: CalendarTask, opts?: { nested?: boolean }) => {
    const overdue = isCalendarTaskOverdue(t, today);
    return (
      <li key={t.id}>
        <button
          type="button"
          className={`cal-task-row cal-task-row--${t.status}${
            selected?.id === t.id ? ' is-selected' : ''
          }${overdue ? ' is-overdue' : ''}${opts?.nested ? ' is-nested' : ''}`}
          onClick={() => setSelected(t)}
        >
          <span className={`cal-status-dot cal-status-dot--${t.status}`} />
          <span className="cal-task-row__body">
            <strong>
              {t.title}
              {overdue ? <span className="cal-overdue-badge">Overdue</span> : null}
              {t.recurrence_series_id ? <span className="cal-repeat-badge">Repeats</span> : null}
            </strong>
            <span>
              {calendarStatusLabel(t.status)} · DL {t.deadline}
              {t.assignee_email ? ` · ${personName(t.assignee_email)}` : ''}
            </span>
          </span>
        </button>
      </li>
    );
  };

  return (
    <div className="cal-page cal-page--tasks">
      <section className="cal-panel cal-panel--list">
        <div className="cal-tasks-head">
          <div>
            <h2>All tasks</h2>
            <p className="cal-tasks-head__meta">
              {filtered.length} of {tasks.length} · any status · {modeLabel}
            </p>
          </div>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={() => setShowCreate((v) => !v)}
          >
            {showCreate ? 'Close' : 'New task'}
          </button>
        </div>

        {error ? <p className="alert alert-error">{error}</p> : null}

        <div className="cal-group-toggle" role="group" aria-label="Group tasks">
          <button
            type="button"
            className={groupMode === 'date' ? 'is-active' : undefined}
            onClick={() => setGroupMode('date')}
          >
            By date
          </button>
          <button
            type="button"
            className={groupMode === 'task' ? 'is-active' : undefined}
            onClick={() => setGroupMode('task')}
          >
            By tasks
          </button>
        </div>

        <div className="cal-search">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, assignee, status, dates…"
            aria-label="Search tasks"
          />
          <div className="cal-search__actions">
            <button type="button" className="cal-link-btn" onClick={expandAll}>
              Expand all
            </button>
            <button type="button" className="cal-link-btn" onClick={collapseAll}>
              Collapse all
            </button>
          </div>
        </div>

        {showCreate ? (
          <div className="cal-create">
            <h3>New task</h3>
            <label className="cal-field">
              <span>Title</span>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="What needs doing?"
              />
            </label>
            <div className="cal-fields-row">
              <label className="cal-field">
                <span>Request</span>
                <input
                  type="date"
                  value={form.request_date}
                  onChange={(e) => setForm({ ...form, request_date: e.target.value })}
                />
              </label>
              <label className="cal-field">
                <span>To-do</span>
                <input
                  type="date"
                  value={form.todo_date}
                  onChange={(e) => setForm({ ...form, todo_date: e.target.value })}
                />
              </label>
              <label className="cal-field">
                <span>Deadline</span>
                <input
                  type="date"
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                />
              </label>
            </div>
            <label className="cal-field">
              <span>Assignee</span>
              {directory.size > 0 ? (
                <select
                  value={form.assignee_email}
                  onChange={(e) => setForm({ ...form, assignee_email: e.target.value })}
                >
                  {[...directory.entries()]
                    .sort((a, b) => a[1].localeCompare(b[1]))
                    .map(([addr, name]) => (
                      <option key={addr} value={addr}>
                        {name}
                      </option>
                    ))}
                  {form.assignee_email && !directory.has(form.assignee_email.toLowerCase()) ? (
                    <option value={form.assignee_email}>{personName(form.assignee_email)}</option>
                  ) : null}
                </select>
              ) : (
                <input
                  value={form.assignee_email}
                  onChange={(e) => setForm({ ...form, assignee_email: e.target.value })}
                />
              )}
            </label>
            <CalendarRecurrenceFields value={recurForm} onChange={setRecurForm} />
            <button
              type="button"
              className="btn-primary"
              disabled={busy || !form.title.trim()}
              onClick={() => void create()}
            >
              Create
            </button>
          </div>
        ) : null}

        <div className="cal-task-groups">
          {(groupMode === 'date' ? monthGroups.length === 0 : taskGroups.length === 0) ? (
            <p className="cal-empty">{query.trim() ? 'No matching tasks.' : 'No tasks yet.'}</p>
          ) : groupMode === 'date' ? (
            monthGroups.map((month) => {
              const monthCollapsed = Boolean(collapsed[month.key]);
              return (
                <section
                  key={month.key}
                  className={`cal-task-group cal-task-group--month${monthCollapsed ? '' : ' is-expanded'}`}
                >
                  <button
                    type="button"
                    className="cal-task-group__head cal-task-group__head--month"
                    onClick={() => toggleGroup(month.key)}
                    aria-expanded={!monthCollapsed}
                  >
                    <span className="cal-task-group__chevron" aria-hidden>
                      {monthCollapsed ? '▸' : '▾'}
                    </span>
                    <span className="cal-task-group__title">{month.title}</span>
                    <span className="cal-task-group__count">{month.count}</span>
                  </button>
                  {!monthCollapsed
                    ? month.days.map((day) => {
                        const dayCollapsed = Boolean(collapsed[day.key]);
                        return (
                          <section
                            key={day.key}
                            className={`cal-task-group cal-task-group--day${dayCollapsed ? '' : ' is-expanded'}`}
                          >
                            <button
                              type="button"
                              className="cal-task-group__head cal-task-group__head--day"
                              onClick={() => toggleGroup(day.key)}
                              aria-expanded={!dayCollapsed}
                            >
                              <span className="cal-task-group__chevron" aria-hidden>
                                {dayCollapsed ? '▸' : '▾'}
                              </span>
                              <span className="cal-task-group__title">{day.title}</span>
                              <span className="cal-task-group__count">{day.count}</span>
                            </button>
                            {!dayCollapsed ? (
                              <ul className="cal-task-list">
                                {day.tasks.map((t) => renderTaskRow(t))}
                              </ul>
                            ) : null}
                          </section>
                        );
                      })
                    : null}
                </section>
              );
            })
          ) : (
            taskGroups.map((group) => {
              const isCollapsed = Boolean(collapsed[group.key]);
              const root = group.root;
              if (!root) return null;
              const rootOverdue = isCalendarTaskOverdue(root, today);
              return (
                <section key={group.key} className="cal-task-group cal-task-group--thread">
                  <button
                    type="button"
                    className="cal-task-group__head"
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={!isCollapsed}
                  >
                    <span className="cal-task-group__chevron" aria-hidden>
                      {isCollapsed ? '▸' : '▾'}
                    </span>
                    <span className="cal-task-group__title">
                      {root.title}
                      {rootOverdue ? <span className="cal-overdue-badge">Overdue</span> : null}
                    </span>
                    <span className="cal-task-group__meta">
                      Req {group.requestDate}
                      {group.subtasks.length > 0
                        ? ` · ${group.subtasks.length} subtask${group.subtasks.length === 1 ? '' : 's'}`
                        : ''}
                    </span>
                    <span className="cal-task-group__count">{group.count}</span>
                  </button>
                  {!isCollapsed ? (
                    <ul className="cal-task-list">
                      {renderTaskRow(root)}
                      {group.subtasks.map((t) => renderTaskRow(t, { nested: true }))}
                    </ul>
                  ) : null}
                </section>
              );
            })
          )}
        </div>
      </section>

      <section className="cal-panel cal-panel--detail">
        {view ? (
          <>
            <div className="cal-detail-head">
              <h2>Detail</h2>
              {!editing ? (
                <button type="button" className="cal-link-btn" onClick={() => setEditing(true)}>
                  Edit
                </button>
              ) : (
                <button type="button" className="cal-link-btn" onClick={cancelEdit}>
                  Cancel
                </button>
              )}
            </div>

            {editing ? (
              <>
                <label className="cal-field">
                  <span>Title</span>
                  <input
                    value={view.title}
                    onChange={(e) => setDraft({ ...view, title: e.target.value })}
                  />
                </label>
                <label className="cal-field">
                  <span>Description</span>
                  <textarea
                    rows={4}
                    value={view.description}
                    onChange={(e) => setDraft({ ...view, description: e.target.value })}
                  />
                </label>
                <div className="cal-fields-row">
                  <label className="cal-field">
                    <span>Request date</span>
                    <input
                      type="date"
                      value={view.request_date}
                      onChange={(e) => setDraft({ ...view, request_date: e.target.value })}
                    />
                  </label>
                  <label className="cal-field">
                    <span>To-do date</span>
                    <input
                      type="date"
                      value={view.todo_date}
                      onChange={(e) => setDraft({ ...view, todo_date: e.target.value })}
                    />
                  </label>
                  <label className="cal-field">
                    <span>Deadline</span>
                    <input
                      type="date"
                      value={view.deadline}
                      onChange={(e) => setDraft({ ...view, deadline: e.target.value })}
                    />
                  </label>
                </div>
                <div className="cal-fields-row">
                  <label className="cal-field">
                    <span>Status</span>
                    <select
                      value={view.status}
                      onChange={(e) =>
                        setDraft({ ...view, status: e.target.value as CalendarTaskStatus })
                      }
                    >
                      <option value="todo">To do</option>
                      <option value="in_progress">In progress</option>
                      <option value="done">Done</option>
                    </select>
                  </label>
                  <label className="cal-field">
                    <span>Assignee</span>
                    {directory.size > 0 ? (
                      <select
                        value={view.assignee_email}
                        onChange={(e) => setDraft({ ...view, assignee_email: e.target.value })}
                      >
                        {[...directory.entries()]
                          .sort((a, b) => a[1].localeCompare(b[1]))
                          .map(([addr, name]) => (
                            <option key={addr} value={addr}>
                              {name}
                            </option>
                          ))}
                        {view.assignee_email &&
                        !directory.has(view.assignee_email.toLowerCase()) ? (
                          <option value={view.assignee_email}>
                            {personName(view.assignee_email)}
                          </option>
                        ) : null}
                      </select>
                    ) : (
                      <input
                        value={view.assignee_email}
                        onChange={(e) => setDraft({ ...view, assignee_email: e.target.value })}
                      />
                    )}
                  </label>
                </div>
                <CalendarRecurrenceFields value={editRecurForm} onChange={setEditRecurForm} />
                {view.recurrence_series_id ? (
                  <fieldset className="cal-series-scope">
                    <legend>Apply content changes to</legend>
                    <label className="cal-series-scope__option">
                      <input
                        type="radio"
                        name="tasks-series-scope"
                        checked={seriesScope === 'this'}
                        onChange={() => setSeriesScope('this')}
                      />
                      This occurrence only
                    </label>
                    <label className="cal-series-scope__option">
                      <input
                        type="radio"
                        name="tasks-series-scope"
                        checked={seriesScope === 'series'}
                        onChange={() => setSeriesScope('series')}
                      />
                      All occurrences
                    </label>
                    <p className="cal-field__hint">
                      Title, description, assignee, and date offsets follow this choice. Repeat
                      schedule / ending date always updates the whole series. Status stays on this
                      occurrence.
                    </p>
                  </fieldset>
                ) : null}
                <div className="cal-drawer__actions">
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    disabled={busy}
                    onClick={() => void save()}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => void followUp()}
                  >
                    Add follow-up
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm cal-btn-danger"
                    disabled={busy}
                    onClick={() => void remove(view.id)}
                  >
                    Delete
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="cal-readonly">
                  <div className="cal-readonly__block">
                    <span className="cal-readonly__label">Title</span>
                    <p className="cal-readonly__value">
                      {view.title}
                      {isCalendarTaskOverdue(view, today) ? (
                        <span className="cal-overdue-badge">Overdue</span>
                      ) : null}
                    </p>
                  </div>
                  <div className="cal-readonly__block">
                    <span className="cal-readonly__label">Description</span>
                    <p className="cal-readonly__value cal-readonly__value--pre">
                      {view.description.trim() || '—'}
                    </p>
                  </div>
                  <div className="cal-readonly__grid">
                    <div className="cal-readonly__block">
                      <span className="cal-readonly__label">Request date</span>
                      <p className="cal-readonly__value">{view.request_date}</p>
                    </div>
                    <div className="cal-readonly__block">
                      <span className="cal-readonly__label">To-do date</span>
                      <p className="cal-readonly__value">{view.todo_date}</p>
                    </div>
                    <div className="cal-readonly__block">
                      <span className="cal-readonly__label">Deadline</span>
                      <p
                        className={`cal-readonly__value${
                          isCalendarTaskOverdue(view, today) ? ' is-overdue-text' : ''
                        }`}
                      >
                        {view.deadline}
                      </p>
                    </div>
                  </div>
                  <div className="cal-readonly__grid">
                    <div className="cal-readonly__block">
                      <span className="cal-readonly__label">Status</span>
                      <p className="cal-readonly__value">{calendarStatusLabel(view.status)}</p>
                    </div>
                    <div className="cal-readonly__block">
                      <span className="cal-readonly__label">Assignee</span>
                      <p className="cal-readonly__value">{personName(view.assignee_email) || '—'}</p>
                    </div>
                  </div>
                  {view.recurrence_rule || view.recurrence_series_id ? (
                    <div className="cal-readonly__block">
                      <span className="cal-readonly__label">Repeats</span>
                      <p className="cal-readonly__value">
                        {recurrenceRuleLabel(view.recurrence_rule)}
                        {view.recurrence_index > 0
                          ? ` · occurrence #${view.recurrence_index + 1}`
                          : ''}
                      </p>
                    </div>
                  ) : (
                    <div className="cal-readonly__block">
                      <span className="cal-readonly__label">Repeats</span>
                      <p className="cal-readonly__value">Does not repeat</p>
                    </div>
                  )}
                </div>
                <div className="cal-drawer__actions">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => void followUp()}
                  >
                    Add follow-up
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm cal-btn-danger"
                    disabled={busy}
                    onClick={() => void remove(view.id)}
                  >
                    Delete
                  </button>
                </div>
              </>
            )}

            <div className="cal-attach">
              <div className="cal-attach__head">
                <h3 className="cal-timeline__title">Attachments</h3>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={busy || !email}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Attach document
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="cal-attach__input"
                  onChange={(e) => void onAttachFiles(e.target.files)}
                />
              </div>
              {attachments.length === 0 ? (
                <p className="cal-empty cal-empty--tight">No files attached.</p>
              ) : (
                <ul className="cal-attach__list">
                  {attachments.map((att) => (
                    <li key={att.id} className="cal-attach__item">
                      <button
                        type="button"
                        className="cal-attach__name"
                        onClick={() => void openAttachment(att)}
                      >
                        {att.file_name}
                      </button>
                      <span className="cal-attach__meta">{formatBytes(att.byte_size)}</span>
                      <button
                        type="button"
                        className="cal-link-btn cal-attach__remove"
                        disabled={busy}
                        onClick={() => void removeAttachment(att)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <h3 className="cal-timeline__title">Linked timeline</h3>
            <ol className="cal-timeline">
              {sortThreadTasksByDate(timeline).map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className={`cal-timeline__item${t.id === view.id ? ' is-current' : ''}${
                      isCalendarTaskOverdue(t, today) ? ' is-overdue' : ''
                    }`}
                    onClick={() => setSelected(t)}
                  >
                    <span className="cal-timeline__date">{t.todo_date}</span>
                    <span className="cal-timeline__name">
                      {t.title}
                      {isCalendarTaskOverdue(t, today) ? (
                        <span className="cal-overdue-badge">Overdue</span>
                      ) : null}
                    </span>
                    <span className="cal-timeline__meta">
                      {calendarStatusLabel(t.status)} · {personName(t.assignee_email)} · DL{' '}
                      {t.deadline}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </>
        ) : (
          <p className="cal-empty">Select a task to view details.</p>
        )}
      </section>
    </div>
  );
}
